import React from 'react';
import {
	Box,
	Button,
	Checkbox,
	Drawer,
	FormControlLabel,
	Link,
	Typography,
} from '@mui/material';
import CookieIcon from '@mui/icons-material/Cookie';
import config from '../common/config';
import { useCookieConsent } from '../contexts/CookieConsentContext';
import { useAppHeaderAndPromptsVisibility } from '../contexts/AppHeaderAndPromptsVisibilityContext';

/**
 * Small cookie prompt in the bottom corner, layered above the intro dialog without blocking the page.
 * Essential storage is fixed on; analytics is optional via checkbox; one submit saves preferences.
 */
const CookiePromptDialog = () => {
	const {
		cookieDrawerOpen,
		saveCookiePreferences,
		analyticsConsent,
		cookiePromptCompleted,
		cookiePromptFocusNonce,
	} = useCookieConsent();
	const { areAppHeaderAndPromptsHidden } = useAppHeaderAndPromptsVisibility();
	const open = cookieDrawerOpen && !areAppHeaderAndPromptsHidden;
	const [analyticsEnabled, setAnalyticsEnabled] = React.useState(true);
	const paperRef = React.useRef<HTMLDivElement | null>(null);

	React.useEffect(() => {
		if (open) {
			setAnalyticsEnabled(cookiePromptCompleted ? analyticsConsent : true);
		}
	}, [open, analyticsConsent, cookiePromptCompleted]);

	React.useEffect(() => {
		if (cookiePromptFocusNonce === 0 || !open) return;
		const el = paperRef.current;
		if (!el) return;
		el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
		requestAnimationFrame(() => {
			el.focus({ preventScroll: true });
		});
	}, [cookiePromptFocusNonce, open]);

	const handleCloseRequest = () => {
		// Closing requires an explicit choice via the form.
	};

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		saveCookiePreferences(analyticsEnabled, 'save');
	};

	return (
		<Drawer
			anchor='bottom'
			open={open}
			onClose={handleCloseRequest}
			hideBackdrop
			ModalProps={{
				disableEscapeKeyDown: true,
				disableEnforceFocus: true,
				disableAutoFocus: true,
				disableScrollLock: true,
			}}
			slotProps={{
				paper: {
					ref: paperRef,
					tabIndex: -1,
					elevation: 8,
					sx: {
						pointerEvents: 'auto',
						left: { xs: 16, sm: 'auto' },
						right: 16,
						bottom: 'max(16px, env(safe-area-inset-bottom))',
						width: { xs: 'auto', sm: 340 },
						borderRadius: 2,
						p: 2,
					},
				},
			}}
			sx={{
				zIndex: (theme) => theme.zIndex.modal + 2,
				pointerEvents: 'none',
			}}
		>
			<form onSubmit={handleSubmit}>
				<Box display='flex' alignItems='center' gap={0.75} mb={0.5}>
					<CookieIcon fontSize='small' />
					<Typography variant='subtitle2'>Cookies &amp; privacy</Typography>
				</Box>
				<Typography variant='caption' color='text.secondary' component='p'>
					{config.appName} uses essential cookies to run. See our{' '}
					<Link href='/privacy-policy.html' target='_blank' rel='noopener noreferrer'>
						Privacy Policy
					</Link>.
				</Typography>
				<FormControlLabel
					sx={{ mt: 0.5, ml: -0.75 }}
					control={
						<Checkbox
							size='small'
							checked={analyticsEnabled}
							onChange={(_, checked) => setAnalyticsEnabled(checked)}
						/>
					}
					label={<Typography variant='caption'>Allow analytics to help us improve</Typography>}
				/>
				<Box display='flex' justifyContent='flex-end' gap={1} mt={0.5}>
					<Button
						type='button'
						size='small'
						variant='text'
						onClick={() => saveCookiePreferences(false, 'reject-all')}
					>
						Reject all
					</Button>
					<Button type='submit' size='small' variant='contained'>
						{analyticsEnabled ? 'Accept all' : 'Accept essential'}
					</Button>
				</Box>
			</form>
		</Drawer>
	);
};

export default CookiePromptDialog;
